import {
  S3Client,
  PutObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  DeleteObjectsCommand,
} from "@aws-sdk/client-s3";
import { S3_CONFIG } from "./s3Config";
import { quitarDiacriticos } from "./texto";

const cliente = new S3Client({
  region: S3_CONFIG.region,
  credentials: S3_CONFIG.credentials,
  requestChecksumCalculation: "WHEN_REQUIRED",
  responseChecksumValidation: "WHEN_REQUIRED",
});

const PREFIJO_PROYECTOS = "proyectos/";
const PREFIJO_CLIENTES = "clientes/";

const slugificar = (texto) =>
  quitarDiacriticos(texto)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const sufijoAleatorio = () => {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
};

export const generarSlugProyecto = (nombre) => {
  const base = slugificar(nombre) || "proyecto";
  return `${base}-${sufijoAleatorio()}`;
};

export const generarSlugCliente = (nombre) => {
  const base = slugificar(nombre) || "cliente";
  return `${base}-${sufijoAleatorio()}`;
};

const slugificarTitulo = (titulo) => slugificar(titulo) || "paso";

export const rutaArchivoPaso = ({
  proyectoId,
  pasoNumero,
  tituloPaso,
  version,
  tipo,
  nombreArchivo,
}) =>
  `${PREFIJO_PROYECTOS}${proyectoId}/paso-${pasoNumero}-${slugificarTitulo(
    tituloPaso
  )}/v${version}/${tipo}/${nombreArchivo}`;

const rutaMetadata = (proyectoId) => `${PREFIJO_PROYECTOS}${proyectoId}/metadata.json`;

export const guardarMetadataProyecto = async (proyecto) => {
  await cliente.send(
    new PutObjectCommand({
      Bucket: S3_CONFIG.bucket,
      Key: rutaMetadata(proyecto.id),
      Body: JSON.stringify(proyecto),
      ContentType: "application/json",
      CacheControl: "no-store",
    })
  );
};

export const listarProyectos = async () => {
  const respuesta = await cliente.send(
    new ListObjectsV2Command({
      Bucket: S3_CONFIG.bucket,
      Prefix: PREFIJO_PROYECTOS,
      Delimiter: "/",
    })
  );

  const prefijos = (respuesta.CommonPrefixes || []).map((p) => p.Prefix);

  const proyectos = await Promise.all(
    prefijos.map(async (prefijo) => {
      const proyectoId = prefijo.slice(PREFIJO_PROYECTOS.length, -1);
      try {
        const objeto = await cliente.send(new GetObjectCommand({ Bucket: S3_CONFIG.bucket, Key: rutaMetadata(proyectoId) }));
        const texto = await objeto.Body.transformToString();
        return JSON.parse(texto);
      } catch (error) {
        console.error(`No se pudo leer metadata.json de "${proyectoId}".`, error);
        return null;
      }
    })
  );

  return proyectos
    .filter(Boolean)
    .sort((a, b) => new Date(b.fechaCreacion) - new Date(a.fechaCreacion));
};

const rutaMetadataCliente = (clienteId) => `${PREFIJO_CLIENTES}${clienteId}/cliente.json`;

export const guardarMetadataCliente = async (datosCliente) => {
  await cliente.send(
    new PutObjectCommand({
      Bucket: S3_CONFIG.bucket,
      Key: rutaMetadataCliente(datosCliente.id),
      Body: JSON.stringify(datosCliente),
      ContentType: "application/json",
      CacheControl: "no-store",
    })
  );
};

export const listarClientes = async () => {
  const respuesta = await cliente.send(
    new ListObjectsV2Command({
      Bucket: S3_CONFIG.bucket,
      Prefix: PREFIJO_CLIENTES,
      Delimiter: "/",
    })
  );

  const prefijos = (respuesta.CommonPrefixes || []).map((p) => p.Prefix);

  const clientes = await Promise.all(
    prefijos.map(async (prefijo) => {
      const clienteId = prefijo.slice(PREFIJO_CLIENTES.length, -1);
      try {
        const objeto = await cliente.send(new GetObjectCommand({ Bucket: S3_CONFIG.bucket, Key: rutaMetadataCliente(clienteId) }));
        const texto = await objeto.Body.transformToString();
        return JSON.parse(texto);
      } catch (error) {
        console.error(`No se pudo leer cliente.json de "${clienteId}".`, error);
        return null;
      }
    })
  );

  return clientes
    .filter(Boolean)
    .sort((a, b) => new Date(b.fechaCreacion) - new Date(a.fechaCreacion));
};

export const eliminarClienteS3 = async (clienteId) => {
  await cliente.send(
    new DeleteObjectsCommand({
      Bucket: S3_CONFIG.bucket,
      Delete: { Objects: [{ Key: rutaMetadataCliente(clienteId) }] },
    })
  );
};

export const subirArchivoPaso = async ({
  proyectoId,
  pasoNumero,
  tituloPaso,
  version,
  tipo,
  archivo,
  nombreArchivo,
}) => {
  const key = rutaArchivoPaso({
    proyectoId,
    pasoNumero,
    tituloPaso,
    version,
    tipo,
    nombreArchivo: nombreArchivo || archivo.name,
  });
  await cliente.send(
    new PutObjectCommand({
      Bucket: S3_CONFIG.bucket,
      Key: key,
      Body: archivo,
      ContentType: archivo.type || "application/octet-stream",
      CacheControl: "no-store",
    })
  );
  return key;
};

export const listarArchivosPaso = async ({ proyectoId, pasoNumero, tituloPaso, version }) => {
  const prefijo = `${PREFIJO_PROYECTOS}${proyectoId}/paso-${pasoNumero}-${slugificarTitulo(
    tituloPaso
  )}/v${version}/`;
  const respuesta = await cliente.send(
    new ListObjectsV2Command({
      Bucket: S3_CONFIG.bucket,
      Prefix: prefijo,
    })
  );

  return (respuesta.Contents || [])
    .map((objeto) => {
      const resto = objeto.Key.slice(prefijo.length);
      const separador = resto.indexOf("/");
      if (separador === -1) return null;
      return {
        key: objeto.Key,
        tipo: resto.slice(0, separador),
        nombreArchivo: resto.slice(separador + 1),
        tamanio: objeto.Size,
      };
    })
    .filter((archivo) => archivo && archivo.nombreArchivo);
};

export const descargarArchivoS3 = async (key) => {
  const objeto = await cliente.send(new GetObjectCommand({ Bucket: S3_CONFIG.bucket, Key: key }));
  const bytes = await objeto.Body.transformToByteArray();
  return new Blob([bytes], { type: objeto.ContentType || "application/octet-stream" });
};

export const eliminarProyectoS3 = async (proyectoId) => {
  const prefijo = `${PREFIJO_PROYECTOS}${proyectoId}/`;
  let continuationToken;
  const claves = [];

  do {
    const respuesta = await cliente.send(
      new ListObjectsV2Command({
        Bucket: S3_CONFIG.bucket,
        Prefix: prefijo,
        ContinuationToken: continuationToken,
      })
    );
    (respuesta.Contents || []).forEach((objeto) => claves.push({ Key: objeto.Key }));
    continuationToken = respuesta.IsTruncated ? respuesta.NextContinuationToken : undefined;
  } while (continuationToken);

  if (claves.length === 0) return;

  for (let i = 0; i < claves.length; i += 1000) {
    const lote = claves.slice(i, i + 1000);
    await cliente.send(
      new DeleteObjectsCommand({
        Bucket: S3_CONFIG.bucket,
        Delete: { Objects: lote },
      })
    );
  }
};